/* Aprire una foto per ridimensionarla, anche quando e' HEIC.

   Le foto HEIC (iPhone dalla galleria, Samsung con «immagini ad alta
   efficienza») le decodifica solo Safari. Su Chrome, Edge e Firefox
   `new Image()` fallisce con un evento senza messaggio: il caricamento
   moriva in pochi millisecondi con «Riprova piu' tardi», e riprovare non
   serviva mai (STU-450: 38 pagine di un libro, Chrome su Linux, disdetta in
   8 minuti).

   Qui: si prova col browser; se non ce la fa e il file e' HEIC, lo si
   converte in JPEG con libheif (`heic-to`), caricata solo in quel caso. Il
   server apre le immagini con Pillow, che HEIC non lo legge: la conversione
   deve stare qui. */

const HEIC_NOME = /\.(heic|heif)$/i;
const HEIC_TIPO = /^image\/hei[cf](-sequence)?$/i;

export const eHeic = (file) => HEIC_NOME.test(file?.name || '') || HEIC_TIPO.test(file?.type || '');

/* L'errore che chi chiama riconosce per dire la cosa giusta allo studente
   invece di «riprova piu' tardi». */
export class FotoHeicNonLeggibile extends Error {
  constructor(nome) {
    super(`Foto HEIC non leggibile: ${nome || 'senza nome'}`);
    this.name = 'FotoHeicNonLeggibile';
    this.codice = 'heic_non_leggibile';
  }
}

export const eErroreHeic = (errore) => errore?.codice === 'heic_non_leggibile';

const caricaNelBrowser = (file) =>
  new Promise((risolvi, rifiuta) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => risolvi({ img, rilascia: () => URL.revokeObjectURL(url) });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      rifiuta(new Error(`Immagine non leggibile: ${file?.name || 'senza nome'}`));
    };
    img.src = url;
  });

/* La conversione si importa qui dentro e non in cima al file: sono ~3 MB e
   servono solo a chi carica HEIC da un browser che non le apre. */
const convertiInJpeg = async (file) => {
  try {
    const { heicTo } = await import(/* webpackChunkName: "heic-to" */ 'heic-to/csp');
    const jpeg = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 });
    const nome = (file.name || 'foto.heic').replace(HEIC_NOME, '.jpg');
    return new File([jpeg], nome, { type: 'image/jpeg', lastModified: file.lastModified || Date.now() });
  } catch (errore) {
    console.error('Conversione HEIC fallita:', errore);
    throw new FotoHeicNonLeggibile(file?.name);
  }
};

/**
 * Apre una foto in un <img> pronto per il canvas.
 * @returns {Promise<{img: HTMLImageElement, rilascia: () => void}>}
 * @throws {FotoHeicNonLeggibile} se e' HEIC e nemmeno la conversione riesce
 */
export const apriFoto = async (file) => {
  try {
    return await caricaNelBrowser(file);
  } catch (errore) {
    if (!eHeic(file)) throw errore;
    const jpeg = await convertiInJpeg(file);
    try {
      return await caricaNelBrowser(jpeg);
    } catch (_) {
      throw new FotoHeicNonLeggibile(file?.name);
    }
  }
};
